// IssueSidebar.jsx - UPDATED with Reviewer and SHA ID fields
"use client"

import React, { useState, useEffect } from "react"
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Autocomplete,
  Chip,
  Button,
  Tooltip,
  Divider,
} from "@mui/material"
import {
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Add as AddIcon,
  BugReport as BugReportIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Code as CodeIcon, // NEW: For SHA ID

} from "@mui/icons-material"
import RateReviewIcon from '@mui/icons-material/RateReview';  // ✔ Use this instead of ReviewIcon

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const statuses = [
  { id: 1, name: "To Do" },
  { id: 2, name: "In Progress" },
  { id: 3, name: "Done" },
  { id: 4, name: "Blocked" },
]

const bugStatuses = [
  { id: 1, name: "New", color: "#ff4444" },
  { id: 2, name: "Open", color: "#ff8800" },
  { id: 3, name: "In Progress", color: "#0088ff" },
  { id: 4, name: "Resolved", color: "#00aa00" },
  { id: 5, name: "Closed", color: "#888888" },
  { id: 6, name: "Reopened", color: "#ff4444" },
]

const priorities = [
  { id: 1, name: "Highest" },
  { id: 2, name: "High" },
  { id: 3, name: "Medium" },
  { id: 4, name: "Low" },
  { id: 5, name: "Lowest" },
  { id: 6, name: "Normal" },
  { id: 7, name: "P1" },
  { id: 8, name: "P2" },
  { id: 9, name: "P3" },
  { id: 10, name: "P4" },
  { id: 11, name: "P5" },
]

const severities = [
  { id: 1, name: "Trivial" },
  { id: 2, name: "Minor" },
  { id: 3, name: "Major" },
  { id: 4, name: "Critical" },
  { id: 5, name: "Blocker" },
]

const predefinedLabels = [
  { id: 1, name: "Frontend", color: "#0052cc" },
  { id: 2, name: "Backend", color: "#00875a" },
  { id: 3, name: "Design", color: "#8777d9" },
  { id: 4, name: "Documentation", color: "#ff7452" },
  { id: 8, name: "Testing", color: "#805ad5" },
]

const requiredFields = ["status", "assignee", "priority", "startDate", "endDate"]

export const labelColors = [
  { name: "Blue", value: "#0052cc" },
  { name: "Green", value: "#00875a" },
  { name: "Purple", value: "#8777d9" },
  { name: "Orange", value: "#ff7452" },
  { name: "Red", value: "#e53e3e" },
  { name: "Teal", value: "#319795" },
  { name: "Pink", value: "#d53f8c" },
  { name: "Indigo", value: "#553c9a" },
  { name: "Yellow", value: "#d69e2e" },
  { name: "Gray", value: "#718096" },
]

const formatEmployeeName = (emp) => {
  if (!emp) return "Unknown"
  
  const first = emp.firstName ? String(emp.firstName).trim() : ""
  const last = emp.lastName ? String(emp.lastName).trim() : ""
  
  const parts = []
  if (first && first.toLowerCase() !== "null" && first.toLowerCase() !== "undefined") parts.push(first)
  if (last && last.toLowerCase() !== "null" && last.toLowerCase() !== "undefined") parts.push(last)
  
  if (parts.length > 0) {
    return parts.join(" ")
  }
  
  const rawName = emp.name || emp.fullName || "Unknown"
  if (rawName === "Unknown") return "Unknown"
  
  const cleaned = rawName.split(" ")
    .filter(s => s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined')
    .join(" ")
    .trim()
    
  return cleaned || "Unknown"
}

const IssueSidebar = ({
  issueData,
  selectedIssueType,
  teamMembers,
  labels = [],
  isLoading,
  isTeamMembersLoading,
  isProductOwnerLoading,
  errors = {},
  validationErrors = {},
  showValidation = false,
  handleChange,
  handleLabelsChange,
  handleOpenLabelDialog,
}) => {

  // FIXED: Single watcher state instead of array
  const [selectedWatcher, setSelectedWatcher] = useState(null)

  // NEW: Single reviewer state
  const [selectedReviewer, setSelectedReviewer] = useState(null)

  const allLabels = React.useMemo(() => {
    const labelMap = new Map()

    predefinedLabels.forEach((label) => {
      labelMap.set(label.name.toLowerCase(), label)
    })

    if (labels && Array.isArray(labels)) {
      labels.forEach((label) => {
        labelMap.set(label.name.toLowerCase(), label)
      })
    }

    return Array.from(labelMap.values())
  }, [labels])

  const handleFieldChange = (event) => {
    const { name, value, type } = event.target

    let processedValue = value

    if (type === "number") {
      processedValue = value === "" ? "" : Number.parseFloat(value)
    } else if (type === "date") {
      processedValue = value
    }

    if (handleChange) {
      handleChange({
        target: {
          name,
          value: processedValue,
        },
      })
    }
  }

  const handleLabelsChangeLocal = (event, newValue) => {

    if (handleLabelsChange) {
      const syntheticEvent = {
        target: {
          name: "labels",
          value: newValue,
        },
      }

      handleLabelsChange(syntheticEvent, newValue)
    }
  }

  const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0

    const start = new Date(startDate)
    const end = new Date(endDate)

    if (start >= end) return 0

    let workingDays = 0
    const currentDate = new Date(start)

    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay()
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++
      }
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return workingDays
  }

  const handleDateChange = (event) => {
    const { name, value } = event.target

    if (handleChange) {
      handleChange({
        target: {
          name,
          value,
        },
      })
    }

    setTimeout(() => {
      const startDate = name === "startDate" ? value : issueData.startDate
      const endDate = name === "endDate" ? value : issueData.endDate

      if (startDate && endDate) {
        const workingDays = calculateWorkingDays(startDate, endDate)
        const estimatedHours = workingDays * 8

        if (handleChange && estimatedHours > 0) {
          handleChange({
            target: {
              name: "timeEstimate",
              value: estimatedHours,
            },
          })
        }
      }
    }, 0)
  }

  const handleCreateLabelClick = () => {
    if (handleOpenLabelDialog) {
      handleOpenLabelDialog()
    }
  }

  const getSelectedLabels = () => {
    if (!issueData.labels || !Array.isArray(issueData.labels)) {
      return []
    }

    return allLabels.filter((label) => {
      return issueData.labels.some((selectedLabel) => {
        if (typeof selectedLabel === "object") {
          return selectedLabel.id === label.id || selectedLabel.name === label.name
        }
        return selectedLabel === label.name || selectedLabel === label.id
      })
    })
  }

  // FIXED: Get selected watcher (single employee)
  const getSelectedWatcher = () => {
 

    if (!issueData.watcher || !teamMembers || teamMembers.length === 0) {
      return null
    }

    const selectedWatcher = teamMembers.find((member) => {
      const memberId = member.employee_id || member.id || member.employeeId
      const watcherIdStr = String(issueData.watcher).trim()
      const memberIdStr = String(memberId).trim()

      return watcherIdStr === memberIdStr
    })

    return selectedWatcher
  }

  // NEW: Get selected reviewer (single employee)
  const getSelectedReviewer = () => {
  

    if (!issueData.reviewer || !teamMembers || teamMembers.length === 0) {
      return null
    }

    const selectedReviewer = teamMembers.find((member) => {
      const memberId = member.employee_id || member.id || member.employeeId
      const reviewerIdStr = String(issueData.reviewer).trim()
      const memberIdStr = String(memberId).trim()

      return reviewerIdStr === memberIdStr
    })

    return selectedReviewer
  }

  // FIXED: Handle single watcher change
  const handleWatcherChange = (event, newValue) => {
  

    if (handleChange) {
      const watcherId = newValue ? newValue.employee_id || newValue.id || newValue.employeeId : null


      handleChange({
        target: {
          name: "watcher",
          value: watcherId,
        },
      })
    }
  }

  // NEW: Handle single reviewer change
  const handleReviewerChange = (event, newValue) => {
 

    if (handleChange) {
      const reviewerId = newValue ? newValue.employee_id || newValue.id || newValue.employeeId : null


      handleChange({
        target: {
          name: "reviewer",
          value: reviewerId,
        },
      })
    }
  }

  const getWatcherOptionLabel = (option) => {
    if (!option) return "Unknown"

    const name = formatEmployeeName(option)
    const role = option.role || option.position || option.title || "No Role"

    return `${name} - ${role}`
  }

  // NEW: Get reviewer option label (same as watcher)
  const getReviewerOptionLabel = (option) => {
    if (!option) return "Unknown"

    const name = formatEmployeeName(option)
    const role = option.role || option.position || option.title || "No Role"

    return `${name} - ${role}`
  }

  const isWatcherOptionEqual = (option, value) => {
    const optionId = option.employee_id || option.id || option.employeeId
    const valueId = value.employee_id || value.id || value.employeeId

    return String(optionId) === String(valueId)
  }

  // NEW: Is reviewer option equal (same as watcher)
  const isReviewerOptionEqual = (option, value) => {
    const optionId = option.employee_id || option.id || option.employeeId
    const valueId = value.employee_id || value.id || value.employeeId

    return String(optionId) === String(valueId)
  }

  const isFieldRequired = (fieldName) => requiredFields.includes(fieldName)

  const isFieldEmpty = (fieldName) => {
    const fieldValue = issueData[fieldName]
    return !fieldValue || fieldValue === "" || fieldValue === null || fieldValue === undefined
  }

  const shouldShowFieldError = (fieldName) => {
    return showValidation && isFieldRequired(fieldName) && isFieldEmpty(fieldName)
  }

  const getFieldErrorMessage = (fieldName) => {
    if (validationErrors[fieldName]) {
      return validationErrors[fieldName]
    }
    if (shouldShowFieldError(fieldName)) {
      const fieldLabels = {
        status: "Status",
        assignee: "Assignee",
        priority: "Priority",
        startDate: "Start Date",
        endDate: "Due Date",
      }
      return `${fieldLabels[fieldName] || fieldName} is required`
    }
    return ""
  }

  const getErrorStyles = (fieldName) => {
    const hasError = shouldShowFieldError(fieldName)
    return {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "#d32f2f" : undefined,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "#d32f2f" : undefined,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "#d32f2f" : undefined,
      },
    }
  }

  const getErrorLabelStyles = (fieldName) => {
    const hasError = shouldShowFieldError(fieldName)
    return {
      color: hasError ? "#d32f2f" : undefined,
      "&.Mui-focused": {
        color: hasError ? "#d32f2f" : undefined,
      },
    }
  }

  return (
    <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, boxShadow: 2 }}>
      <CardContent sx={{ p: 3 }}>
        {/* Issue ID (Display Only) */}
        {issueData.id && (
          <TextField
            fullWidth
            id="issueId"
            label="Issue ID"
            value={issueData.id}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <AssignmentIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
            helperText="Auto-generated unique identifier"
          />
        )}

        {/* Status Selection */}
        <FormControl fullWidth sx={{ mb: 3 }} error={shouldShowFieldError("status")}>
          <InputLabel id="status-label" sx={getErrorLabelStyles("status")}>
            Status *
          </InputLabel>
          <Select
            labelId="status-label"
            id="status"
            name="status"
            value={issueData.status || ""}
            label="Status"
            onChange={handleFieldChange}
            disabled={isLoading}
            error={shouldShowFieldError("status")}
            sx={getErrorStyles("status")}
          >
            {statuses.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                <Typography>{status.name}</Typography>
              </MenuItem>
            ))}
          </Select>
          {shouldShowFieldError("status") && (
            <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, ml: 1 }}>
              {getFieldErrorMessage("status")}
            </Typography>
          )}
        </FormControl>

        {/* Bug Status and Version Fields - Only show for Bug type */}
        {selectedIssueType && selectedIssueType.name === "Bug" && (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="bug-status-label">Bug Status</InputLabel>
              <Select
                labelId="bug-status-label"
                id="bugStatus"
                name="bugStatus"
                value={issueData.bugStatus || ""}
                label="Bug Status"
                onChange={handleFieldChange}
                disabled={isLoading}
              >
                {bugStatuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: status.color,
                          mr: 1,
                        }}
                      />
                      {status.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* FIXED: Defect Reporter - show employee names from database */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="reporter-label">Defect Reporter</InputLabel>
              <Select
                labelId="reporter-label"
                id="reporter"
                name="reporter"
                value={issueData.reporter || ""}
                onChange={handleFieldChange}
                label="Defect Reporter"
                disabled={isLoading || isTeamMembersLoading || teamMembers.length === 0}
                startAdornment={
                  <InputAdornment position="start">
                    <BugReportIcon />
                  </InputAdornment>
                }
              >
                {teamMembers && teamMembers.length > 0 ? (
                  teamMembers.map((member) => {
                    const memberName = formatEmployeeName(member)
                    const memberId = member.employee_id || member.id || member.employeeId
                    const memberRole = member.role || member.position || member.title || "No Role"
                    return (
                      <MenuItem key={memberId} value={memberId}>
                        {`${memberName} - ${memberRole}`}
                      </MenuItem>
                    )
                  })
                ) : (
                  <MenuItem disabled value="">
                    {isTeamMembersLoading ? "Loading team members..." : "No team members available"}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              id="version"
              name="version"
              label="Version (Bug found in)"
              value={issueData.version || ""}
              onChange={handleFieldChange}
              placeholder="e.g., v1.2.3, Release 2.1, Build 456"
              disabled={isLoading}
              sx={{ mb: 3 }}
              helperText="Software version where the bug was found"
            />
<TextField
      fullWidth
      id="foundInBuild"
      name="foundInBuild"
      label="Found in Build"
      value={issueData.foundInBuild || ""}
      onChange={handleFieldChange}
      placeholder="e.g., Build 1234, CI-5678, Nightly-2024-01-15"
      disabled={isLoading}
      sx={{ mb: 3 }}
      helperText="Specific build number where the bug was first observed"
    />
            <TextField
              fullWidth
              id="fixedInVersion"
              name="fixedInVersion"
              label="Fixed in Version"
              value={issueData.fixedInVersion || ""}
              onChange={handleFieldChange}
              placeholder="e.g., v1.2.4, Release 2.2, Build 457"
              disabled={isLoading}
              sx={{ mb: 3 }}
            />
             <TextField
                  fullWidth
                  id="fixedInBuild"
                  name="fixedInBuild"
                  label="Fixed in Build"
                  value={issueData.fixedInBuild || ""}
                  onChange={handleFieldChange}
                  placeholder="e.g., Build 1235, CI-5679, Hotfix-2024-01-16"
                  disabled={isLoading}
                  sx={{ mb: 3 }}
                  helperText="Specific build number containing the fix"
                />
          </>
        )}

        {/* Issue Type Display */}
        {selectedIssueType && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Issue Type
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {selectedIssueType.icon}
              <Typography sx={{ ml: 1 }}>{selectedIssueType.name}</Typography>
            </Box>
          </Box>
        )}
<FormControl fullWidth sx={{ mb: 3 }} error={shouldShowFieldError("lead")}>
  <InputLabel id="lead-label" sx={getErrorLabelStyles("lead")}>
    Lead *
  </InputLabel>
  <Select
    labelId="lead-label"
    id="lead"
    name="lead"
    value={issueData.lead || ""}
    onChange={handleFieldChange}
    label="Lead"
    disabled={isLoading || isTeamMembersLoading}
    error={shouldShowFieldError("lead")}
    sx={getErrorStyles("lead")}
    startAdornment={
      <InputAdornment position="start">
        <PersonIcon />
      </InputAdornment>
    }
  >
    {teamMembers && teamMembers.length > 0 ? (
              teamMembers.map((member) => {
                const memberName = formatEmployeeName(member)
                const memberId = member.employee_id || member.id || member.employeeId
                const memberRole = member.role || member.position || member.title || "No Role"
                return (
                  <MenuItem key={memberId} value={memberId}>
                    {`${memberName} - ${memberRole}`}
                  </MenuItem>
                )
              })
            ) : (
              <MenuItem disabled value="">
                {isTeamMembersLoading ? "Loading team members..." : "No team members available"}
              </MenuItem>
            )}
  </Select>

  {shouldShowFieldError("lead") && (
    <Typography
      variant="caption"
      sx={{ color: "#d32f2f", mt: 0.5, ml: 1 }}
    >
      {getFieldErrorMessage("lead")}
    </Typography>
  )}
</FormControl>
        {/* FIXED: Assignee - store name instead of ID */}
        <FormControl fullWidth sx={{ mb: 3 }} error={shouldShowFieldError("assignee")}>
          <InputLabel id="assignee-label" sx={getErrorLabelStyles("assignee")}>
            Assignee *
          </InputLabel>
          <Select
            labelId="assignee-label"
            id="assignee"
            name="assignee"
            value={issueData.assignee || ""}
            onChange={handleFieldChange}
            label="Assignee"
            disabled={isLoading || isTeamMembersLoading || teamMembers.length === 0}
            error={shouldShowFieldError("assignee")}
            startAdornment={
              <InputAdornment position="start">
                <PersonIcon />
              </InputAdornment>
            }
            sx={getErrorStyles("assignee")}
          >
            {teamMembers && teamMembers.length > 0 ? (
              teamMembers.map((member) => {
                const memberName = formatEmployeeName(member)
                const memberId = member.employee_id || member.id || member.employeeId
                const memberRole = member.role || member.position || member.title || "No Role"
                return (
                  <MenuItem key={memberId} value={memberId}>
                    {`${memberName} - ${memberRole}`}
                  </MenuItem>
                )
              })
            ) : (
              <MenuItem disabled value="">
                {isTeamMembersLoading ? "Loading team members..." : "No team members available"}
              </MenuItem>
            )}
          </Select>
          {shouldShowFieldError("assignee") && (
            <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, ml: 1 }}>
              {getFieldErrorMessage("assignee")}
            </Typography>
          )}
          {teamMembers && teamMembers.length === 0 && !isTeamMembersLoading && (
            <Typography color="warning.main" variant="caption">
              No team members available for this project.
            </Typography>
          )}
        </FormControl>

        {/* NEW: Reviewer Selection - Single employee, similar to Watcher */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <Autocomplete
            id="reviewer"
            options={teamMembers || []}
            getOptionLabel={getReviewerOptionLabel}
            isOptionEqualToValue={isReviewerOptionEqual}
            value={getSelectedReviewer()}
            onChange={handleReviewerChange}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Reviewer"
                placeholder="Select one employee to review this issue"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <RateReviewIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            disabled={isLoading || isTeamMembersLoading || teamMembers.length === 0}
            noOptionsText={isTeamMembersLoading ? "Loading team members..." : "No team members available"}
          />
        </FormControl>

        {/* FIXED: Single Watcher selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <Autocomplete
            id="watcher"
            options={teamMembers || []}
            getOptionLabel={getWatcherOptionLabel}
            isOptionEqualToValue={isWatcherOptionEqual}
            value={getSelectedWatcher()}
            onChange={handleWatcherChange}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Watcher"
                placeholder="Select one employee to watch this issue"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <VisibilityIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            disabled={isLoading || isTeamMembersLoading || teamMembers.length === 0}
            noOptionsText={isTeamMembersLoading ? "Loading team members..." : "No team members available"}
          />
        </FormControl>

        {/* Product Owner (Read-Only) */}
        <TextField
          id="productOwnerName"
          label="Product Owner"
          value={
            issueData.productOwnerName 
              ? issueData.productOwnerName.split(" ").filter(s => s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined').join(" ").trim() || "Not assigned"
              : "Not assigned"
          }
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
          helperText="Automatically assigned from project"
          disabled={isProductOwnerLoading}
        />

        {/* FIXED: Reporter field - only show for Bug type */}
        {selectedIssueType && selectedIssueType.name === "Bug" && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="reporter-label">Reporter</InputLabel>
            <Select
              labelId="reporter-label"
              id="reporter"
              name="reporter"
              value={issueData.reporter || ""}
              onChange={handleFieldChange}
              label="Reporter"
              disabled={isLoading || isTeamMembersLoading || teamMembers.length === 0}
              startAdornment={
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              }
            >
              {teamMembers && teamMembers.length > 0 ? (
                teamMembers.map((member) => {
                  const memberName = formatEmployeeName(member)
                  const memberId = member.employee_id || member.id || member.employeeId
                  const memberRole = member.role || member.position || member.title || "No Role"
                  return (
                    <MenuItem key={memberId} value={memberId}>
                      {`${memberName} - ${memberRole}`}
                    </MenuItem>
                  )
                })
              ) : (
                <MenuItem disabled value="">
                  {isTeamMembersLoading ? "Loading team members..." : "No team members available"}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Priority */}
        <FormControl fullWidth sx={{ mb: 3 }} error={shouldShowFieldError("priority")}>
          <InputLabel id="priority-label" sx={getErrorLabelStyles("priority")}>
            Priority *
          </InputLabel>
          <Select
            labelId="priority-label"
            id="priority"
            name="priority"
            value={issueData.priority || ""}
            label="Priority"
            onChange={handleFieldChange}
            disabled={isLoading}
            error={shouldShowFieldError("priority")}
            sx={getErrorStyles("priority")}
          >
            {priorities.map((priority) => (
              <MenuItem key={priority.id} value={priority.id}>
                {priority.name}
              </MenuItem>
            ))}
          </Select>
          {shouldShowFieldError("priority") && (
            <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, ml: 1 }}>
              {getFieldErrorMessage("priority")}
            </Typography>
          )}
        </FormControl>

        {/* Severity */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="severity-label">Severity</InputLabel>
          <Select
            labelId="severity-label"
            id="severity"
            name="severity"
            value={issueData.severity || ""}
            label="Severity"
            onChange={handleFieldChange}
            disabled={isLoading}
          >
            {severities.map((severity) => (
              <MenuItem key={severity.id} value={severity.id}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography>{severity.name}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        {/* Date Fields */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              id="startDate"
              name="startDate"
              label="Start Date *"
              type="date"
              value={issueData.startDate || ""}
              onChange={handleDateChange}
              error={shouldShowFieldError("startDate")}
              InputLabelProps={{
                shrink: true,
                sx: getErrorLabelStyles("startDate"),
              }}
              // InputProps={{
              //   inputProps: {
              //     min: new Date().toISOString().split("T")[0],
              //   },
              // }}
              sx={getErrorStyles("startDate")}
              disabled={isLoading}
            />
            {shouldShowFieldError("startDate") && (
              <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, ml: 1, display: "block" }}>
                {getFieldErrorMessage("startDate")}
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              id="endDate"
              name="endDate"
              label="Due Date *"
              type="date"
              value={issueData.endDate || ""}
              onChange={handleDateChange}
              error={shouldShowFieldError("endDate")}
              InputLabelProps={{
                shrink: true,
                sx: getErrorLabelStyles("endDate"),
              }}
              InputProps={{
                inputProps: {
                  min: issueData.startDate || new Date().toISOString().split("T")[0],
                },
              }}
              sx={getErrorStyles("endDate")}
              disabled={isLoading}
            />
            {shouldShowFieldError("endDate") && (
              <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, ml: 1, display: "block" }}>
                {getFieldErrorMessage("endDate")}
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Time Estimate */}
        <TextField
          id="timeEstimate"
          name="timeEstimate"
          label="Time Estimate (hours)"
          type="number"
          value={issueData.timeEstimate || ""}
          onChange={handleFieldChange}
          fullWidth
          InputProps={{
            inputProps: { min: 0, step: 0.5 },
            startAdornment: (
              <InputAdornment position="start">
                <AccessTimeIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
          disabled={isLoading}
        />

        {/* FIXED: Test Coverage field */}
        <TextField
          id="testCoverage"
          name="testCoverage"
          label="Test Coverage (%)"
          type="number"
          value={issueData.testCoverage || ""}
          onChange={handleFieldChange}
          fullWidth
          InputProps={{
            inputProps: { min: 0, max: 100, step: 1 },
            startAdornment: (
              <InputAdornment position="start">
                <AssessmentIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
          disabled={isLoading}
          helperText="Percentage of code covered by tests"
        />

        {/* NEW: SHA ID Field - Text input for Git commit SHA */}
        <TextField
          id="shaId"
          name="shaId"
          label="SHA ID (Git Commit)"
          value={issueData.shaId || ""}
          onChange={handleFieldChange}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CodeIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
          disabled={isLoading}
          helperText="Git commit SHA hash associated with this issue (e.g., a1b2c3d4...)"
        />

        <Divider sx={{ my: 3 }} />

        {/* Labels with Autocomplete */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <Autocomplete
            multiple
            id="labels"
            options={allLabels}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id || option.name === value.name
            }}
            value={getSelectedLabels()}
            onChange={handleLabelsChangeLocal}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box
                  component="span"
                  sx={{
                    width: 14,
                    height: 14,
                    mr: 1,
                    borderRadius: "50%",
                    display: "inline-block",
                    bgcolor: option.color || "#0052cc",
                  }}
                />
                {option.name}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="filled"
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option.id || index}
                  style={{
                    backgroundColor: option.color || "#0052cc",
                    color: "white",
                    marginRight: 5,
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Labels"
                placeholder="Select labels or type to search"
                helperText={`${allLabels.length} labels available (including predefined ones)`}
              />
            )}
            disabled={isLoading}
            noOptionsText="No labels match your search"
            loading={isLoading}
            loadingText="Loading labels..."
          />
        </FormControl>

        {/* Add new label button */}
        <Tooltip title="Create a new label">
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateLabelClick}
            fullWidth
            sx={{ mb: 2 }}
            disabled={isLoading}
          >
            Create New Label
          </Button>
        </Tooltip>
      </CardContent>
    </Card>
  )
}

export default IssueSidebar